import React from "react";
import { FormSectionElements, FormSubSections, ProgramContainer } from "../../entities/formTemplateDataType";
import { InputText } from "primereact/inputtext";
import { Dropdown } from "primereact/dropdown";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { DialogBase } from "./DialogBase";

interface Props {
    programContainer: ProgramContainer;
    ele: FormSectionElements;
    isEng: boolean;
    callback: () => void;
}

interface State {
    availableFields: { code: string; description: string }[];
}

export class TableSummaryDesignComponent extends DialogBase<Props, State> {
    constructor(props: Props) {
        super(props);
        
        this.state = {
            availableFields: []
        };
    }

    componentDidMount() {
        this.collectAvailableFields();
        this.migrateLegacyFormats();
    }

    migrateLegacyFormats = () => {
        const ele = this.props.ele;
        if (!ele.TargetFields) {
            ele.TargetFields = [];
        }
        
        if (ele.TargetFieldIds && ele.TargetFieldIds.length > 0 && ele.TargetFields.length === 0) {
            ele.TargetFields = ele.TargetFieldIds.map(fieldId => ({
                fieldId: fieldId,
                customLabel: undefined
            }));
        } else if (ele.TargetFieldId && ele.TargetFields.length === 0) {
            ele.TargetFields = [{
                fieldId: ele.TargetFieldId,
                customLabel: undefined
            }];
        }
        
        if (ele.TargetFields.length === 0) {
            ele.TargetFields.push({
                fieldId: '',
                customLabel: undefined
            });
        }
    };

    collectAvailableFields = () => {
        const fields: { code: string; description: string }[] = [];
        const currentSubsection = this.props.ele.parent as FormSubSections;
        const currentSection = currentSubsection.parent;
        const sections = this.props.programContainer.ListOfFormSections.filter(
            s => s.Sequence <= currentSection.Sequence
        );
        
        sections.forEach(section => {
            section.ListOfFormSubSections.forEach(subsection => {
                if (
                    section.Sequence < currentSection.Sequence || 
                    (
                        section.Sequence === currentSection.Sequence && 
                        subsection.Sequence <= currentSubsection.Sequence
                    )
                ) {
                    
                    subsection.ListOfFormSectionElements.forEach(element => {
                        if (element.Id !== this.props.ele.Id && 
                            element.Datatype !== 'Button' && 
                            element.Datatype !== 'Label' &&
                            element.Datatype !== 'TableSummary') {
                            
                            const title = element.ENUTitle;
                            if (title) {
                                fields.push({
                                    code: element.Id,
                                    description: `${title} (${element.Datatype})`
                                });
                            }
                        }
                        
                        if (element.subSectionList) {
                            element.subSectionList.forEach(subSec => {
                                subSec.ListOfFormSectionElements.forEach(nestedElement => {
                                    if (nestedElement.Datatype !== 'Button' && 
                                        nestedElement.Datatype !== 'Label') {
                                        const title = nestedElement.ENUTitle;
                                        if (title) {
                                            fields.push({
                                                code: nestedElement.Id,
                                                description: `${title} (${nestedElement.Datatype})`
                                            });
                                        }
                                    }
                                });
                            });
                        }
                    });
                }
            });
        });
        
        this.setState({ availableFields: fields });
    };

    addTargetField = () => {
        const ele = this.props.ele;
        if (!ele.TargetFields) {
            ele.TargetFields = [];
        }
        
        ele.TargetFields.push({
            fieldId: '',
            customLabel: undefined
        });
        
        this.props.callback();
    };

    removeTargetField = (index: number) => {
        const ele = this.props.ele;
        if (ele.TargetFields && ele.TargetFields.length > index) {
            ele.TargetFields.splice(index, 1);
            
            if (ele.TargetFields.length === 0) {
                ele.TargetFields.push({
                    fieldId: '',
                    customLabel: undefined
                });
            }
            
            this.props.callback();
        }
    };

    updateFieldId = (index: number, fieldId: string) => {
        const ele = this.props.ele;
        if (ele.TargetFields && ele.TargetFields.length > index) {
            ele.TargetFields[index].fieldId = fieldId;
            
            if (index === 0) {
                ele.TargetFieldId = fieldId;
            }
            
            this.props.callback();
        }
    };

    updateCustomLabel = (index: number, label: string) => {
        const ele = this.props.ele;
        if (ele.TargetFields && ele.TargetFields.length > index) {
            ele.TargetFields[index].customLabel = label || undefined;
            this.props.callback();
        }
    };

    renderSectionHeader = (title: string) => {
        return (
            <h3 style={{ marginTop: 0, marginBottom: '10px', fontSize: '1.1em' }}>
                {title}
            </h3>
        );
    };

    render() {
        const ele = this.props.ele;
        const targetFields = ele.TargetFields || [];
        
        return (
            <div className="ontario-div" style={{ paddingTop: '10px' }}>
                <div style={{ marginBottom: '25px', paddingBottom: '20px', borderBottom: '1px solid #ddd' }}>
                    {this.renderSectionHeader('Table Summary Title')}
                    <div style={{ marginBottom: '10px' }}>
                        <label 
                            htmlFor="tableSummaryTitle"
                            style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '0.9em' }}
                        >
                            Title
                        </label>
                        <InputText
                            id="tableSummaryTitle"
                            className="ontario-input"
                            style={{ width: '100%' }}
                            value={ele.ENUTitle || ''}
                            onChange={(e) => {
                                ele.ENUTitle = e.target.value;
                                this.props.callback();
                            }}
                        />
                    </div>
                </div>
                <div style={{ marginBottom: '10px' }}>
                    {this.renderSectionHeader('Target Fields to Display')}
                    {targetFields.map((targetField, index) => (
                        <div 
                            key={`target-field-${index}`} 
                            style={{ 
                                marginBottom: '10px', 
                                padding: '15px', 
                                backgroundColor: '#f9f9f9',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                <h4 style={{ margin: 0, fontSize: '0.95em', fontWeight: 600 }}>
                                    Field {index + 1}
                                </h4>
                                {targetFields.length > 1 && (
                                    <Button 
                                        icon="pi pi-trash" 
                                        className="p-button-danger p-button-text p-button-sm"
                                        onClick={() => this.removeTargetField(index)}
                                        tooltip="Remove this field"
                                        tooltipOptions={{ position: 'left' }}
                                    />
                                )}
                            </div>
                            
                            <div style={{ marginBottom: '10px' }}>
                                <label 
                                    htmlFor={`targetField-${index}`} 
                                    style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '0.9em' }}
                                >
                                    Select Field *
                                </label>
                                <Dropdown
                                    id={`targetField-${index}`}
                                    className="ontario-dropdown"
                                    style={{ width: '100%' }}
                                    options={this.state.availableFields}
                                    optionLabel="description"
                                    optionValue="code"
                                    value={targetField.fieldId || ''}
                                    onChange={(e) => this.updateFieldId(index, e.value)}
                                    filter
                                    filterBy="description"
                                    showClear
                                />
                            </div>
                            
                            <div style={{ marginBottom: '10px' }}>
                                <label 
                                    htmlFor={`customLabel-${index}`}
                                    style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold', fontSize: '0.9em' }}
                                >
                                    Custom Label - Optional
                                </label>
                                <InputText
                                    id={`customLabel-${index}`}
                                    className="ontario-input"
                                    style={{ width: '100%' }}
                                    value={targetField.customLabel || ''}
                                    onChange={(e) => this.updateCustomLabel(index, e.target.value)}
                                />
                            </div>
                        </div>
                    ))}
                    
                    <Button 
                        icon="pi pi-plus" 
                        label="Add Another Field"
                        className="p-button-outlined"
                        onClick={this.addTargetField}
                        style={{ marginTop: '10px' }}
                    />
                </div>
            </div>
        );
    }
}
